import { fetchExperienceById } from 'actions/ExperienceActions'
import { useContext, useEffect } from 'react'
import { useAppDispatch } from 'hooks/reduxHooks'
import ExperienceOfferPageContent from './ExperienceOfferPageContent'
import ExperienceOfferPageLoadingSkeleton from './ExperienceOfferPageLoadingSkeleton'
import Error404PageWithOffers from 'components/Pages/Error404PageWithOffers/Error404PageWithOffers'
import Error500Page from 'components/Pages/Error500Page'
import { useParams } from 'react-router'
import RedirectWithStatus from 'components/Common/RedirectWithStatus/RedirectWithStatus'
import { getExperienceOfferPageURL } from 'lib/offer/offerPageURL'
import ExperienceOfferPageStateProvider from 'contexts/Experience/ExperienceOfferPage/experienceOfferPageStateProvider'
import useExperiencePageReducer from 'hooks/ExperiencePage/useExperiencePageReducer'
import AnalyticsPageContext, { AnalyticsPage } from 'contexts/Analytics/analyticsPageContext'
import ModalProvider from 'contexts/ModalProvider'
import GeoContext from 'contexts/geoContext'
import useExperience from 'hooks/Experiences/useExperience'
import useOfferPageWalledGarden from 'hooks/OfferPage/useOfferPageWalledGarden'
import AccountAccessModal from 'components/Account/AccountAccess/AccountAccessModal'
import ModalContext from 'contexts/ModalContext'
import { isWhitelabelBrand } from 'lib/whitelabels/whitelabels'
import config from 'constants/config'

function ExperienceOfferPage() {
  const { id, slug } = useParams<{ id: string, slug: string }>()
  const [experience,, error] = useExperience(id)
  const [state, stateDispatch] = useExperiencePageReducer(experience)
  const { currentRegionCode } = useContext(GeoContext)
  const dispatch = useAppDispatch()
  const showModal = useContext(ModalContext)

  // Only show walled garden modal for whitelabel brands
  // Non-whitelabel brands keeps using soft walled garden
  const bypassWalledGardenModal = !isWhitelabelBrand(config.BRAND)

  const showWall = useOfferPageWalledGarden(bypassWalledGardenModal)

  useEffect(() => {
    dispatch(fetchExperienceById(id))
  }, [dispatch, id])

  useEffect(() => {
    if (showWall) {
      let closeModal: any
      showModal((resolve) => {
        closeModal = resolve
        return <AccountAccessModal dismissable={false} />
      })

      return () => {
        // unmounting, must've changed pages via back button or some other way
        // We need to close the 'wall' as it's no longer applicable on other pages
        closeModal({ loggedIn: false })
      }
    }
  }, [showModal, showWall])

  if (error) {
    if (error.status === 404) {
      return <Error404PageWithOffers />
    }

    return <Error500Page error={error} />
  }

  if (experience && (experience.slug !== slug)) {
    return <RedirectWithStatus code={301} to={`/${currentRegionCode.toLowerCase()}${getExperienceOfferPageURL(experience)}`} />
  }

  return <AnalyticsPageContext.Provider value={AnalyticsPage.offerPage}>
    {!experience && <ExperienceOfferPageLoadingSkeleton />}
    {experience && <ExperienceOfferPageStateProvider state={state} dispatch={stateDispatch}>
      <ModalProvider>
        <ExperienceOfferPageContent experience={experience} />
      </ModalProvider>
    </ExperienceOfferPageStateProvider>}
  </AnalyticsPageContext.Provider>
}

export default ExperienceOfferPage
